import type { SectionType } from './pages';

export const SECTION_CONTENT_WIDTHS = ['narrow', 'standard', 'wide', 'full'] as const;
export const SECTION_BACKGROUND_POSITIONS = [
  'top left', 'top center', 'top right', 'center left', 'center', 'center right',
  'bottom left', 'bottom center', 'bottom right',
] as const;
export const GRID_SECTION_TYPES: readonly SectionType[] = [
  'features', 'stats', 'steps', 'pricing', 'gallery', 'team', 'testimonials', 'modules',
];

/** Optional local overrides. Absent fields keep the page preset's styling. */
export interface SectionDesign {
  background_color?: string;
  text_color?: string;
  heading_color?: string;
  button_color?: string;
  content_width?: (typeof SECTION_CONTENT_WIDTHS)[number];
  padding_top?: number;
  padding_bottom?: number;
  columns?: 1 | 2 | 3 | 4;
  mobile_padding_top?: number;
  mobile_padding_bottom?: number;
  mobile_columns?: 1 | 2;
  background_position?: (typeof SECTION_BACKGROUND_POSITIONS)[number];
}

/** Untrusted saved content never becomes arbitrary CSS. Empty/invalid input is
 *  undefined so old pages keep their original markup and default presentation. */
export function normalizeSectionDesign(raw: unknown): SectionDesign | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const value = raw as Record<string, unknown>;
  const out: SectionDesign = {};
  for (const key of ['background_color', 'text_color', 'heading_color', 'button_color'] as const) {
    const color = value[key];
    if (typeof color !== 'string') continue;
    const match = /^#([\da-f]{3}|[\da-f]{6})$/i.exec(color.trim());
    if (!match) continue;
    const hex = match[1].toLowerCase();
    out[key] = `#${hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex}`;
  }
  for (const key of ['padding_top', 'padding_bottom', 'mobile_padding_top', 'mobile_padding_bottom'] as const) {
    const n = value[key];
    const max = key.startsWith('mobile_') ? 100 : 160;
    if (typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= max) out[key] = n;
  }
  if (typeof value.content_width === 'string' && SECTION_CONTENT_WIDTHS.includes(value.content_width as SectionDesign['content_width'] & string)) {
    out.content_width = value.content_width as SectionDesign['content_width'];
  }
  if (typeof value.background_position === 'string' && SECTION_BACKGROUND_POSITIONS.includes(value.background_position as SectionDesign['background_position'] & string)) {
    out.background_position = value.background_position as SectionDesign['background_position'];
  }
  if (typeof value.columns === 'number' && Number.isInteger(value.columns) && value.columns >= 1 && value.columns <= 4) out.columns = value.columns as SectionDesign['columns'];
  if (value.mobile_columns === 1 || value.mobile_columns === 2) out.mobile_columns = value.mobile_columns;
  return Object.keys(out).length ? out : undefined;
}
